import React, { useCallback } from 'react';

import Typography from 'antd/es/typography';
import Modal from 'antd/es/modal';

import colors from 'config/colors';
import useModal from 'hooks/useModal';

import { ResourceType } from 'services/resources';
import useResource from 'hooks/useResource';
import useAdminResource from '../hooks/useAdminResource';

import AdminLayout from 'layouts/Admin.layout';
import { Row, Col, Space } from 'components/layout';
import { Title } from 'components/Text';
import FormModal from 'components/FormModal';
import Table from 'components/Table';
import Button from 'components/Button';
import Search from 'components/Search';

import { EditAction, DeleteAction } from '../components/TableActions';
import ServerForm from '../components/forms/Server.form';
import ServerInfo from '../components/ServerInfo';

import { mapLocationsToOptions } from 'helpers/mapToOptions';

import ServerSvg from '../assets/images/server.svg';

const ServersPage = () => {
  const infoModal = useModal();
  const locations = useResource(ResourceType.locations, { prefetch: true });
  const servers = useAdminResource({
    resource: ResourceType.devices,
    resourceName: 'server',
    searchFields: [
      'server_id',
      'device_name',
      'location_name',
    ],
  });

  const onAction = useCallback((actionType, item) => {
    switch (actionType) {
      case EditAction.actionType:
        servers.onEdit(item);
        break
      case DeleteAction.actionType:
        servers.onDelete(item.device_id);
        break;
      default: // make eslint happy :)
    }
  }, [servers]);

  return (
    <AdminLayout
      title="Servers"
      icon="cpu"
      extra={[
        <Search
          placeholder={servers.strings.searchPlaceholder}
          value={servers.searchVal}
          onChange={value => servers.search(value)}
        />,
        <Button
          type="primary"
          icon="plus"
          onClick={() => {
            servers.onCreate({ is_active: 1 });
          }}
        >
          {servers.strings.addButton}
        </Button>
      ]}
    >
      <FormModal
        title={servers.strings.formTitle}
        form={servers.form}
        submitText={servers.strings.formSubmitButton}
        onClose={() => servers.form.reset()}
        disabledButtons={servers.form.isSubmitting}
      >
        <ServerForm form={servers.form} locationOptions={mapLocationsToOptions(locations.state.list)} />
      </FormModal>
      <Table
        loading={servers.isLoading}
        dataSource={servers.list}
        actions={[
          EditAction,
          DeleteAction,
        ]}
        actionsProps={{
          resource: 'server',
        }}
        onAction={onAction}
        rowKey="device_id"
      >
        <Table.Column title="ID" dataIndex="device_id" />
        <Table.Column title="Name" dataIndex="device_name"
          render={deviceName => (
            <Typography.Link as="span" style={{ color: colors.primary }}>
              {deviceName}
            </Typography.Link>
          )}
          onCell={item => {
            return {
              onClick: () => {
                infoModal.show(item);
              }
            };
          }
          } />
        <Table.Column title="Model" dataIndex="model" />
        <Table.Column title="RAM" dataIndex="ram" />
        <Table.Column title="App Version" dataIndex="app_version" />
      </Table>

      <Modal
        title={'Server'}
        visible={infoModal.isVisible}
        onCancel={infoModal.hide}
        footer={
          <>
            <Button
              icon="edit"
              onClick={() => {
                infoModal.hide();
                servers.onEdit(infoModal.data);
              }}
            >
              Edit
            </Button>
            <Button
              onClick={() => {
                infoModal.hide();
              }}
            >
              Close
            </Button>
          </>
        }
      >
        <Row style={{ padding: '0 24px' }}>
          <Col span={24}>
            <Space size="large" style={{ marginBottom: '24px' }}>
              <img alt="server vector" src={ServerSvg} />
              <Title level={4}>
                {infoModal.data && infoModal.data.device_name}
              </Title>
            </Space>
            <ServerInfo server={infoModal.data} />
          </Col>
        </Row>
      </Modal>

    </AdminLayout>
  );
};

export default ServersPage;
